import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Plus, Trash2, Play, CheckCircle2, XCircle, ClipboardList, Tag, Package, ChevronDown, ChevronRight, ChefHat, CalendarClock, Factory, Link2, PackageCheck } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { fmtDate, fmtDateTime, fmtNum, statusLabel, dlcStatus, dlcBadgeClass } from "../lib/format";
import { useConfirm } from "../components/ConfirmDialog";
import { pdf } from "@react-pdf/renderer";
import { OfKitchenDocument } from "../components/OfKitchenPdf";
import { showPdfPreview } from "../components/PdfPreview";
import { toast } from "sonner";
import { COMPANY_HEADER } from "../lib/company";

const statusTone = {
  PLANIFIE: "bg-[#F0F0EB] text-[#575A57] border-[#E5E5E0]",
  EN_COURS: "bg-[#EAF3EE] text-[#1F4E3D] border-[#C5DBCF]",
  TERMINE: "bg-[#EBF2E9] text-[#2D6A4F] border-[#C7DDC0]",
  ANNULE: "bg-[#FCEEEA] text-[#C05C47] border-[#E8C1B4]",
};

const newLine = () => ({ product_id: "", quantity_target: 0, lot_number: "", dlc: "" });

export default function Orders() {
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [lotCounts, setLotCounts] = useState({});
  const [clientOrders, setClientOrders] = useState([]);
  const [settings, setSettings] = useState({});
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ reference: "", planned_date: "", notes: "", lines: [newLine()] });
  const [expanded, setExpanded] = useState(new Set());
  // Phase 101cq (Feb 2026) — Filtres de recherche OF (produit + plage date fabrication).
  // Inclut les DLC dépassées : aucun filtre implicite sur la DLC. Comparaison
  // de dates en YYYY-MM-DD (string ordonné correctement).
  const [filterProductId, setFilterProductId] = useState("all");
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo, setFilterDateTo] = useState("");
  const confirm = useConfirm();
  const location = useLocation();
  const navigate = useNavigate();

  const autoRef = () => {
    const today = new Date();
    const y = String(today.getFullYear()).slice(-2);
    const m = String(today.getMonth() + 1).padStart(2, "0");
    const d = String(today.getDate()).padStart(2, "0");
    const n = String(orders.length + 1).padStart(3, "0");
    return `OF-${y}${m}${d}-${n}`;
  };

  const load = async () => {
    const [p, o, l, st, co] = await Promise.all([
      api.get("/products"),
      api.get("/orders"),
      api.get("/lots/counts-by-lot-number"),
      api.get("/settings").catch(() => ({ data: {} })),
      api.get("/client-orders").catch(() => ({ data: [] })),
    ]);
    setProducts(p.data); setOrders(o.data); setLotCounts(l.data || {}); setSettings(st.data || {});
    setClientOrders(co.data || []);
  };
  useEffect(() => { load(); }, []);

  // Pre-fill form via URL: /ordres?prefill=PRODUCT_ID:QTY,PRODUCT_ID:QTY
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const prefill = params.get("prefill");
    if (prefill && products.length > 0) {
      const lines = prefill.split(",").map((entry) => {
        const [pid, qty] = entry.split(":");
        return { product_id: pid, quantity_target: Number(qty) || 0, lot_number: "", dlc: "" };
      }).filter((ln) => products.some((p) => p.id === ln.product_id));
      if (lines.length > 0) {
        setForm({ reference: autoRef(), planned_date: "", notes: "Issu de la planification anticipée", lines });
        setOpen(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, products]);

  const productMap = useMemo(() => Object.fromEntries(products.map((p) => [p.id, p])), [products]);

  // Phase 87t (16/06/2026) — Map des commandes client pour afficher la référence
  // (ex: WEB-260615-2EA060 ou CMD-260615-001) directement sur chaque ligne OF.
  // Permet à l'opérateur de production de savoir au premier coup d'œil à quelle
  // commande web/B2B chaque OF est rattaché, sans avoir à ouvrir le détail.
  const clientOrderMap = useMemo(
    () => Object.fromEntries((clientOrders || []).map((co) => [co.id, co])),
    [clientOrders]
  );

  // Phase 87z (17/06/2026) — Classification visuelle des OFs par type
  // (INTERNE / REVENDEUR / WEB). Permet de différencier d'un coup d'œil dans la
  // liste les OFs internes (réappro distributeurs auto), les OFs B2B revendeurs,
  // et les OFs commandes boutique web.
  const getOrderType = (of) => {
    if (!of.client_order_id) return { kind: "INTERNE", label: "INTERNE", color: "green" };
    const co = clientOrderMap[of.client_order_id];
    if (!co) return { kind: "INTERNE", label: "INTERNE", color: "green" };
    if (co.distributor_id === "shop-online-virtual") {
      return { kind: "WEB", label: `WEB · ${co.reference}`, color: "blue", coId: co.id, coRef: co.reference };
    }
    if (co.reseller_id) {
      return { kind: "REVENDEUR", label: `REVENDEUR · ${(co.distributor_name || "").trim() || co.reference}`, color: "gold", coId: co.id, coRef: co.reference };
    }
    // Commande distributeur classique sans reseller_id : considéré comme "interne dédié"
    return { kind: "INTERNE_CMD", label: `INTERNE · ${co.reference}`, color: "green", coId: co.id, coRef: co.reference };
  };

  const typeBadgeClasses = {
    blue: "bg-[#E6EEF7] border-[#3B5B7A]/40 text-[#3B5B7A]",
    gold: "bg-[#FBF4E9] border-[#8C6B2F]/40 text-[#8C6B2F]",
    green: "bg-[#EBF2E9] border-[#2D6A4F]/40 text-[#2D6A4F]",
  };

  // Phase 87q (15/06/2026) — Filtrage par commande client si `?co=<co_id>` dans l'URL.
  // Permet à "Ouvrir la liste des 3 OF liés" depuis ClientOrderDetail de
  // n'afficher QUE les OF de cette commande (au lieu de tous les OF).
  const coFilterId = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("co") || "";
  }, [location.search]);
  const [coFilterLabel, setCoFilterLabel] = useState("");
  useEffect(() => {
    if (!coFilterId) { setCoFilterLabel(""); return; }
    api.get(`/client-orders/${coFilterId}`).then(({ data }) => {
      setCoFilterLabel(data?.reference || coFilterId);
    }).catch(() => setCoFilterLabel(coFilterId));
  }, [coFilterId]);
  const visibleOrders = useMemo(() => {
    let result = coFilterId ? orders.filter((o) => o.client_order_id === coFilterId) : orders;

    // Phase 101cq — Filtre par produit : garde les OF dont AU MOINS UNE ligne
    // contient le produit sélectionné (option a : un produit à la fois).
    if (filterProductId && filterProductId !== "all") {
      result = result.filter((o) =>
        (o.lines || []).some((ln) => ln.product_id === filterProductId)
      );
    }

    // Phase 101cq — Filtre par plage de dates de fabrication.
    // Utilise `actual_production_date` (date réelle de fabrication, saisissable
    // rétroactivement) sinon fallback sur `planned_date`. Les deux sont en
    // YYYY-MM-DD, donc la comparaison lexicographique fonctionne.
    if (filterDateFrom || filterDateTo) {
      result = result.filter((o) => {
        const fab = (o.actual_production_date || o.planned_date || "").slice(0, 10);
        if (!fab) return false;
        if (filterDateFrom && fab < filterDateFrom) return false;
        if (filterDateTo && fab > filterDateTo) return false;
        return true;
      });
    }

    return result;
  }, [orders, coFilterId, filterProductId, filterDateFrom, filterDateTo]);

  const activeFilterCount =
    (filterProductId && filterProductId !== "all" ? 1 : 0) +
    (filterDateFrom ? 1 : 0) +
    (filterDateTo ? 1 : 0);
  const resetFilters = () => {
    setFilterProductId("all");
    setFilterDateFrom("");
    setFilterDateTo("");
  };

  // Stats par OF : total barquettes prévues, total restantes (qty>0)
  const orderStats = useMemo(() => {
    const map = {};
    for (const o of orders) {
      const lotNumbers = (o.lines || []).map((ln) => ln.lot_number).filter(Boolean);
      const total_target = (o.lines || []).reduce((s, ln) => s + (Number(ln.quantity_target) || 0), 0);
      map[o.id] = {
        total_target,
        total_lots: [...new Set(lotNumbers)].reduce((s, n) => s + (lotCounts[n]?.total || 0), 0),
        remaining_lots: [...new Set(lotNumbers)].reduce((s, n) => s + (lotCounts[n]?.remaining || 0), 0),
      };
    }
    return map;
  }, [orders, lotCounts]);

  // Stats par ligne (lot_number → {total, remaining})
  const lineStatsByLotNumber = lotCounts;

  const computeDlc = (productId, planned) => {
    const p = productMap[productId];
    if (!p?.shelf_life_days || !planned) return "";
    const d = new Date(planned);
    if (isNaN(d.getTime())) return "";
    d.setDate(d.getDate() + Number(p.shelf_life_days));
    return d.toISOString().slice(0, 10);
  };

  const updateLine = (i, patch) => {
    setForm((prev) => {
      const lines = prev.lines.map((ln, idx) => {
        if (idx !== i) return ln;
        const next = { ...ln, ...patch };
        // Auto-DLC quand le produit ou la date prévue change
        if ("product_id" in patch || (prev.planned_date && !next.dlc)) {
          const auto = computeDlc(next.product_id, prev.planned_date);
          if (auto && !next.dlc) next.dlc = auto;
        }
        return next;
      });
      return { ...prev, lines };
    });
  };

  const updatePlannedDate = (v) => {
    setForm((prev) => {
      const lines = prev.lines.map((ln) => {
        if (ln.dlc) return ln; // ne pas écraser une DLC déjà saisie
        const auto = computeDlc(ln.product_id, v);
        return auto ? { ...ln, dlc: auto } : ln;
      });
      return { ...prev, planned_date: v, lines };
    });
  };

  const addLine = () => setForm((p) => ({ ...p, lines: [...p.lines, newLine()] }));
  const removeLine = (i) => setForm((p) => ({ ...p, lines: p.lines.filter((_, idx) => idx !== i) }));

  const autoLot = (suffix) => {
    const today = new Date();
    const y = String(today.getFullYear()).slice(-2);
    const m = String(today.getMonth() + 1).padStart(2, "0");
    const d = String(today.getDate()).padStart(2, "0");
    return `L${suffix || ""}-${y}${m}${d}`;
  };

  const openCreate = () => {
    const today = new Date().toISOString().slice(0, 10);
    setForm({ reference: autoRef(), planned_date: "", actual_production_date: today, notes: "", lines: [newLine()] });
    setOpen(true);
  };

  const save = async () => {
    if (!form.reference) { toast.error("Référence requise"); return; }
    const validLines = form.lines.filter((ln) => ln.product_id && Number(ln.quantity_target) > 0);
    if (validLines.length === 0) { toast.error("Au moins une ligne avec produit + quantité"); return; }
    const payload = {
      reference: form.reference,
      planned_date: form.planned_date || null,
      actual_production_date: form.actual_production_date || null,
      notes: form.notes,
      lines: validLines.map((ln, idx) => ({
        product_id: ln.product_id,
        quantity_target: Number(ln.quantity_target),
        lot_number: ln.lot_number || autoLot(`-${String(idx + 1).padStart(2, "0")}`),
        dlc: ln.dlc || null,
      })),
    };
    try {
      await api.post("/orders", payload);
      toast.success(`OF créé (${validLines.length} produit${validLines.length > 1 ? "s" : ""})`);
      setOpen(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const changeStatus = async (order, status) => {
    await api.put(`/orders/${order.id}`, { status });
    toast.success(`Statut: ${statusLabel[status]}`);
    await load();
  };

  // Phase 87x (16/06/2026) — One-click "Finaliser & Expédier" pour les OFs web.
  // Workflow optimisé pour les commandes boutique web (B2C) qui partent directement
  // après production, sans passage par le stock :
  //   1. Clôture l'OF (PUT status=TERMINE) → crée les lots dédiés en stock
  //   2. Génère immédiatement le BL depuis la commande liée → consomme ces lots
  //   3. Affiche un toast avec lien direct vers le BL pour impression
  // Visible uniquement pour les OFs liés à une commande boutique web.
  const [finalizingId, setFinalizingId] = useState(null);
  const finalizeAndShip = async (order) => {
    if (finalizingId) return;
    if (!order.client_order_id) {
      toast.error("Cet OF n'est pas lié à une commande");
      return;
    }
    setFinalizingId(order.id);
    try {
      if (order.status !== "TERMINE") {
        await api.put(`/orders/${order.id}`, { status: "TERMINE" });
      }
      const { data } = await api.post(`/client-orders/${order.client_order_id}/generate-bl`);
      toast.success(
        `OF clôturé et BL généré (${data?.reference || ""})`,
        { duration: 6000 }
      );
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de la finalisation");
    } finally {
      setFinalizingId(null);
    }
  };

  const remove = async (id) => {
    const ok = await confirm({
      title: "Supprimer cet ordre de fabrication ?",
      description: "Les lots déjà créés ne seront pas supprimés.",
      destructive: true,
      confirmText: "Supprimer",
    });
    if (!ok) return;
    try {
      await api.delete(`/orders/${id}`);
      toast.success("OF supprimé");
    } catch (e) {
      if (e?.response?.status !== 409) {
        toast.error(e?.response?.data?.detail || "Suppression impossible");
        return;
      }
      const again = await confirm({
        title: "OF déjà lancé ou terminé",
        description: "Pour le supprimer, il faut d'abord l'annuler : les lots qu'il a créés seront retirés du stock (refusé si des barquettes ont déjà été livrées ou vendues). Annuler puis supprimer ?",
        destructive: true,
        confirmText: "Annuler et supprimer",
      });
      if (!again) return;
      try {
        await api.put(`/orders/${id}`, { status: "ANNULE" });
        await api.delete(`/orders/${id}`);
        toast.success("OF annulé et supprimé");
      } catch (e2) {
        toast.error(e2?.response?.data?.detail || "Annulation impossible");
      }
    }
    await load();
  };

  const printKitchenSheet = async (order) => {
    try {
      const apiBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
      const blob = await pdf(
        <OfKitchenDocument
          order={order}
          products={products}
          apiBase={apiBase}
          companyName={COMPANY_HEADER}
        />
      ).toBlob();
      showPdfPreview(blob, `fiche-cuisine-${order.reference}.pdf`);
    } catch (e) {
      toast.error("Erreur génération fiche cuisine");
      console.error(e);
    }
  };

  const toggleExpand = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  // Édition rapide de la DLC d'une ligne d'OF (avant clôture)
  const [editingDlcKey, setEditingDlcKey] = useState(null); // "of_id|line_idx"
  const [dlcDraft, setDlcDraft] = useState("");

  // Édition rapide de la quantité d'une ligne d'OF (statut PLANIFIE ou EN_COURS)
  const [editingQtyKey, setEditingQtyKey] = useState(null); // "of_id|line_idx"
  const [qtyDraft, setQtyDraft] = useState("");

  const startEditQty = (order, lineIdx) => {
    const ln = (order.lines || [])[lineIdx] || {};
    setQtyDraft(String(ln.quantity_target ?? ""));
    setEditingQtyKey(`${order.id}|${lineIdx}`);
  };

  const saveQty = async (order, lineIdx) => {
    const newQty = Number(qtyDraft);
    if (!Number.isFinite(newQty) || newQty < 0) {
      toast.error("Quantité invalide");
      return;
    }
    const currentLine = (order.lines || [])[lineIdx] || {};
    const produced = Number(currentLine.quantity_produced || 0);
    if (newQty < produced) {
      toast.error(`Nouvelle quantité (${newQty}) inférieure à la quantité déjà produite (${produced}). Ajustez plutôt la quantité produite ou annulez l'OF.`);
      return;
    }
    try {
      const newLines = (order.lines || []).map((ln, i) => ({
        ...ln,
        quantity_target: i === lineIdx ? newQty : ln.quantity_target,
      }));
      await api.put(`/orders/${order.id}`, { lines: newLines.map((ln) => ({
        product_id: ln.product_id,
        quantity_target: ln.quantity_target,
        quantity_produced: ln.quantity_produced,
        lot_number: ln.lot_number,
        dlc: ln.dlc || null,
      })) });
      toast.success("Quantité mise à jour");
      setEditingQtyKey(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const startEditDlc = (order, lineIdx) => {
    const ln = (order.lines || [])[lineIdx] || {};
    let initial = ln.dlc || "";
    if (!initial) {
      // Pré-remplit avec aujourd'hui + shelf_life_days du produit
      const p = productMap[ln.product_id];
      if (p?.shelf_life_days) {
        const d = new Date();
        d.setDate(d.getDate() + Number(p.shelf_life_days));
        initial = d.toISOString().slice(0, 10);
      }
    }
    setDlcDraft(initial);
    setEditingDlcKey(`${order.id}|${lineIdx}`);
  };
  const saveDlc = async (order, lineIdx) => {
    const newDlc = dlcDraft || null;
    try {
      const newLines = (order.lines || []).map((ln, i) => ({ ...ln, dlc: i === lineIdx ? newDlc : ln.dlc }));
      // L'API PUT /orders/{id} accepte un payload partiel via ProductionOrderUpdate.lines
      await api.put(`/orders/${order.id}`, { lines: newLines.map((ln) => ({
        product_id: ln.product_id,
        quantity_target: ln.quantity_target,
        quantity_produced: ln.quantity_produced,
        lot_number: ln.lot_number,
        dlc: ln.dlc || null,
      })) });
      toast.success("DLC mise à jour pour cette ligne");
      setEditingDlcKey(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const renderDlc = (dlc) => {
    if (!dlc) return <span className="text-[#A3A6A3]">—</span>;
    const s = dlcStatus(dlc);
    if (!s || s.kind === "ok") return <span className="font-mono text-xs">{fmtDate(dlc)}</span>;
    const label = s.kind === "expired"
      ? `Dépassée${Math.abs(s.days) > 0 ? ` (J${s.days})` : ""}`
      : `J-${s.days}`;
    return (
      <Badge variant="outline" className={`${dlcBadgeClass(s.kind)} font-medium gap-1 text-xs`} title={fmtDate(dlc)}>
        {fmtDate(dlc)} · {label}
      </Badge>
    );
  };

  // Phase 87ab (17/06/2026) — DLC visible sur l'expanded de l'OF même avant clôture.
  // Calcule la DLC à partir de production_date + product.shelf_life_days si la ligne
  // n'a pas de DLC stockée. Affichée en italique + tooltip pour distinguer "calculée"
  // de "saisie manuellement" (cliquer le crayon pour figer la valeur).
  const computeEffectiveDlc = (line, of, product) => {
    if (line?.dlc) return { dlc: line.dlc, computed: false };
    if (!of || !product?.shelf_life_days) return { dlc: null, computed: false };
    const prodIso = (of.actual_production_date || of.completed_at || of.planned_date || "").slice(0, 10);
    if (!prodIso) return { dlc: null, computed: false };
    const d = new Date(prodIso + "T00:00:00");
    if (Number.isNaN(d.getTime())) return { dlc: null, computed: false };
    d.setDate(d.getDate() + Number(product.shelf_life_days));
    return { dlc: d.toISOString().slice(0, 10), computed: true };
  };

  const renderDlcOf = (line, of, product) => {
    const { dlc, computed } = computeEffectiveDlc(line, of, product);
    if (!dlc) return <span className="text-[#A3A6A3]">—</span>;
    const inner = renderDlc(dlc);
    if (!computed) return inner;
    return (
      <span
        className="italic opacity-90"
        title={`DLC calculée : date de production planifiée (${fmtDate((of.actual_production_date || of.completed_at || of.planned_date || "").slice(0, 10))}) + ${product.shelf_life_days} j de DLC produit. Cliquer le crayon pour figer une valeur.`}
      >
        {inner}
      </span>
    );
  };

  return (
    <div className="space-y-6" data-testid="orders-page">
      {/* Phase 87q — Bannière filtre commande client */}
      {coFilterId && (
        <div className="flex items-center justify-between gap-3 px-4 py-3 rounded border-2 border-[#8C6B2F] bg-[#FBF4E9]" data-testid="orders-co-filter-banner">
          <div className="text-sm text-[#5C4A1E]">
            <strong>Filtré :</strong> {visibleOrders.length} OF lié{visibleOrders.length > 1 ? "s" : ""} à la commande{" "}
            <Link to={`/commandes-clients/${coFilterId}`} className="font-mono font-semibold underline">
              {coFilterLabel || coFilterId}
            </Link>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={() => navigate("/ordres")}
            className="border-[#8C6B2F] text-[#8C6B2F] hover:bg-[#FBE9CC]"
            data-testid="orders-co-filter-clear"
          >
            Voir tous les OF
          </Button>
        </div>
      )}

      {/* Phase 101cq (Feb 2026) — Recherche OF (type de plat + plage de dates de fabrication).
          Inclut les OF avec DLC dépassée : aucun filtre implicite sur la DLC. */}
      <div className="border border-[#E5E5E0] bg-[#FAFAF7] rounded p-3" data-testid="orders-search-bar">
        <div className="flex items-end gap-3 flex-wrap">
          <div className="flex-1 min-w-[260px]">
            <Label className="text-[10px] uppercase tracking-wider text-[#575A57] mb-1 block">
              Type de plat
            </Label>
            <Select value={filterProductId} onValueChange={setFilterProductId}>
              <SelectTrigger data-testid="orders-filter-product">
                <SelectValue placeholder="Tous les plats" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les plats</SelectItem>
                {[...products]
                  .sort((a, b) => (a.name || "").localeCompare(b.name || "", "fr", { sensitivity: "base" }))
                  .map((pr) => (
                    <SelectItem key={pr.id} value={pr.id}>
                      {pr.name} {pr.sku ? `(${pr.sku})` : ""}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>
          <div className="w-[170px]">
            <Label className="text-[10px] uppercase tracking-wider text-[#575A57] mb-1 block">
              Fabriqué du
            </Label>
            <Input
              type="date"
              value={filterDateFrom}
              onChange={(e) => setFilterDateFrom(e.target.value)}
              data-testid="orders-filter-date-from"
            />
          </div>
          <div className="w-[170px]">
            <Label className="text-[10px] uppercase tracking-wider text-[#575A57] mb-1 block">
              Fabriqué au
            </Label>
            <Input
              type="date"
              value={filterDateTo}
              onChange={(e) => setFilterDateTo(e.target.value)}
              data-testid="orders-filter-date-to"
            />
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={resetFilters}
            disabled={activeFilterCount === 0}
            className="text-[#575A57]"
            data-testid="orders-filter-reset"
            title="Effacer les filtres de recherche"
          >
            Réinitialiser
          </Button>
          <div className="ml-auto text-xs text-[#575A57] pb-2 font-mono">
            {activeFilterCount > 0
              ? `${visibleOrders.length} OF trouvé${visibleOrders.length > 1 ? "s" : ""} sur ${orders.length}`
              : `${orders.length} OF au total`}
          </div>
        </div>
        {activeFilterCount > 0 && visibleOrders.length === 0 && (
          <div className="mt-3 pt-3 border-t border-[#E5E5E0] text-sm text-[#8C6B2F]" data-testid="orders-filter-no-result">
            Aucun OF ne correspond aux filtres. Ajustez la plage de dates ou changez le plat.
          </div>
        )}
      </div>

      <div className="flex items-center justify-between gap-4">
        <div className="text-sm text-[#575A57]">
          <ClipboardList className="w-4 h-4 inline mr-2 -mt-0.5" />
          {coFilterId ? `${visibleOrders.length} OF affiché${visibleOrders.length > 1 ? "s" : ""} sur ${orders.length}` : `${orders.length} ordre${orders.length > 1 ? "s" : ""} au total`}
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={() => navigate("/ordres/planning")}
                data-testid="open-planning-btn"
                title="Vue Gantt — planning temporel des OF"
              >
                <Factory className="w-4 h-4 mr-2" /> Planning
              </Button>
              <Button onClick={openCreate} className="bg-[#1F4E3D] hover:bg-[#173B2E]" data-testid="create-order-btn">
                <Plus className="w-4 h-4 mr-2" /> Nouvel OF
              </Button>
            </div>
          </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col overflow-hidden">
            <DialogHeader className="flex-shrink-0"><DialogTitle className="font-heading">Nouvel ordre de fabrication</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2 overflow-y-auto flex-1 pr-2 -mr-2">
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="mb-1.5 block">Référence *</Label>
                  <Input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} data-testid="of-reference" />
                </div>
                <div>
                  <Label className="mb-1.5 block">Date prévue</Label>
                  <Input type="date" value={form.planned_date} onChange={(e) => updatePlannedDate(e.target.value)} data-testid="of-date" />
                </div>
                <div>
                  <Label className="mb-1.5 block flex items-center gap-1">
                    Date fabrication réelle
                    <span className="text-[10px] text-[#A3A6A3] font-normal">(pour DLC)</span>
                  </Label>
                  <Input
                    type="date"
                    value={form.actual_production_date || ""}
                    onChange={(e) => setForm({ ...form, actual_production_date: e.target.value })}
                    data-testid="of-actual-prod-date"
                    title="Date à laquelle les plats ont été RÉELLEMENT fabriqués. Sert au calcul DLC = cette date + shelf_life_days. Utile pour la saisie rétroactive (OF clôturé aujourd'hui pour production d'hier)."
                  />
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="font-heading text-base">Produits à fabriquer</Label>
                  <Button variant="outline" size="sm" onClick={addLine} data-testid="of-add-line">
                    <Plus className="w-4 h-4 mr-1" /> Ligne
                  </Button>
                </div>
                {form.lines.length === 0 ? (
                  <p className="text-sm text-[#A3A6A3] py-3">Aucune ligne. Cliquez sur « Ligne » pour ajouter un produit.</p>
                ) : (
                  <div className="space-y-2">
                    {form.lines.map((ln, i) => {
                      const p = productMap[ln.product_id];
                      return (
                        <div key={i} className="grid grid-cols-12 gap-2 items-start bg-[#F9F9F6] border border-[#E5E5E0] rounded-md p-2.5" data-testid={`of-line-${i}`}>
                          <div className="col-span-1 flex items-center justify-center pt-2 font-mono text-[#575A57] text-sm font-bold">
                            {i + 1}.
                          </div>
                          <div className="col-span-4">
                            <Label className="text-[10px] uppercase tracking-wider text-[#575A57] mb-0.5 block">Produit</Label>
                            <Select value={ln.product_id} onValueChange={(v) => updateLine(i, { product_id: v })}>
                              <SelectTrigger data-testid={`of-line-product-${i}`}><SelectValue placeholder="Sélectionner…" /></SelectTrigger>
                              <SelectContent>{[...products].sort((a, b) => (a.name || "").localeCompare(b.name || "", "fr", { sensitivity: "base" })).map((pr) => <SelectItem key={pr.id} value={pr.id}>{pr.name} ({pr.sku})</SelectItem>)}</SelectContent>
                            </Select>
                          </div>
                          <div className="col-span-1">
                            <Label className="text-[10px] uppercase tracking-wider text-[#575A57] mb-0.5 block">Qté</Label>
                            <Input type="number" min="0" value={ln.quantity_target} onChange={(e) => updateLine(i, { quantity_target: e.target.value })} data-testid={`of-line-qty-${i}`} />
                          </div>
                          <div className="col-span-3">
                            <Label className="text-[10px] uppercase tracking-wider text-[#575A57] mb-0.5 block">N° lot</Label>
                            <Input value={ln.lot_number} onChange={(e) => updateLine(i, { lot_number: e.target.value })} placeholder="Auto" data-testid={`of-line-lot-${i}`} />
                          </div>
                          <div className="col-span-2">
                            <Label className="text-[10px] uppercase tracking-wider text-[#575A57] mb-0.5 block">
                              DLC{p?.shelf_life_days ? <span className="text-[#1F4E3D] normal-case"> · +{p.shelf_life_days}j</span> : null}
                            </Label>
                            <Input type="date" value={ln.dlc || ""} onChange={(e) => updateLine(i, { dlc: e.target.value })} data-testid={`of-line-dlc-${i}`} />
                          </div>
                          <div className="col-span-1 flex justify-end pt-5">
                            <Button variant="ghost" size="icon" onClick={() => removeLine(i)} disabled={form.lines.length <= 1} data-testid={`of-line-remove-${i}`}>
                              <Trash2 className="w-4 h-4 text-[#C05C47]" />
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <div>
                <Label className="mb-1.5 block">Notes</Label>
                <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              </div>
            </div>
            <DialogFooter className="flex-shrink-0 border-t border-[#E5E5E0] pt-3 mt-1">
              <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
              <Button onClick={save} className="bg-[#1F4E3D] hover:bg-[#173B2E]" data-testid="save-order-btn">Créer l&apos;OF</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white border border-[#E5E5E0] rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-[#F4F4F0] hover:bg-[#F4F4F0]">
              <TableHead className="w-8"></TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold text-[#575A57]">Référence</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold text-[#575A57]">Produits</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold text-[#575A57] text-right">Total qté</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold text-[#575A57]">Date prévue</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold text-[#575A57]">Statut</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold text-[#575A57]">Lots produits</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold text-[#575A57]">Progression</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold text-[#575A57] text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody data-testid="orders-table-body">
            {visibleOrders.length === 0 ? (
              <TableRow><TableCell colSpan={9} className="text-center text-[#575A57] py-10">{coFilterId ? "Aucun OF lié à cette commande." : "Aucun ordre de fabrication."}</TableCell></TableRow>
            ) : visibleOrders.map((o) => {
              const stats = orderStats[o.id] || {};
              const isExpanded = expanded.has(o.id);
              const lines = o.lines || [];
              const productNames = lines.map((ln) => productMap[ln.product_id]?.name || "—");
              const productSummary = productNames.length === 0
                ? "—"
                : productNames.length === 1
                  ? productNames[0]
                  : `${productNames[0]} +${productNames.length - 1}`;
              return (
                <React.Fragment key={o.id}>
                  <TableRow data-testid={`order-row-${o.reference}`} className={lines.length >= 1 ? "cursor-pointer" : ""} onClick={lines.length >= 1 ? () => toggleExpand(o.id) : undefined}>
                    <TableCell className="pr-0">
                      {lines.length >= 1 ? (
                        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={(e) => { e.stopPropagation(); toggleExpand(o.id); }} data-testid={`expand-order-${o.reference}`}>
                          {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                        </Button>
                      ) : null}
                    </TableCell>
                    <TableCell className="font-mono font-medium">
                      <div className="flex flex-col gap-1">
                        <span>{o.reference}</span>
                        {(() => {
                          const t = getOrderType(o);
                          const badgeCls = `inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-mono font-medium border w-fit ${typeBadgeClasses[t.color]}`;
                          if (t.coId) {
                            return (
                              <Link
                                to={`/commandes-clients/${t.coId}`}
                                onClick={(e) => e.stopPropagation()}
                                className={`${badgeCls} hover:underline`}
                                title={`Voir la commande ${t.coRef}`}
                                data-testid={`of-type-${t.kind.toLowerCase()}-${o.reference}`}
                              >
                                <Link2 className="w-2.5 h-2.5" />
                                {t.label}
                              </Link>
                            );
                          }
                          return (
                            <span
                              className={badgeCls}
                              title="OF interne (réapprovisionnement stock distributeurs auto)"
                              data-testid={`of-type-interne-${o.reference}`}
                            >
                              <Factory className="w-2.5 h-2.5" />
                              {t.label}
                            </span>
                          );
                        })()}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span>{productSummary}</span>
                        {lines.length > 1 && (
                          <Badge variant="outline" className="bg-[#F0F0EB] border-[#E5E5E0] text-[#575A57] text-[10px]">
                            {lines.length} lignes
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right font-mono">{fmtNum(stats.total_target)}</TableCell>
                    <TableCell className="font-mono text-xs">
                      <div>{fmtDate(o.planned_date)}</div>
                      {o.actual_production_date && o.actual_production_date.slice(0, 10) !== (o.planned_date || "").slice(0, 10) && (
                        <div className="text-[10px] text-[#8C6B2F] mt-0.5" title="Date fabrication réelle (utilisée pour DLC)">
                          Fab. réelle: {fmtDate(o.actual_production_date)}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={`${statusTone[o.status]} font-medium`}>{statusLabel[o.status]}</Badge>
                    </TableCell>
                    <TableCell>
                      {stats.total_lots > 0 ? (
                        <span className="inline-flex items-center gap-1.5 text-sm font-mono">
                          <Package className="w-3.5 h-3.5 text-[#1F4E3D]" />
                          <span className="font-semibold text-[#1F4E3D]">{stats.remaining_lots}</span>
                          <span className="text-[#A3A6A3]">/ {stats.total_lots}</span>
                        </span>
                      ) : (
                        <span className="text-xs text-[#A3A6A3]">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-[#575A57]">
                      {o.started_at && <div className="text-xs">Démarré: {fmtDateTime(o.started_at)}</div>}
                      {o.completed_at && o.status !== "ANNULE" && <div className="text-xs text-[#2D6A4F]">Terminé: {fmtDateTime(o.completed_at)}</div>}
                      {o.status === "ANNULE" && o.cancelled_at && (
                        <div className="text-xs text-[#C05C47]" data-testid={`of-cancelled-${o.reference}`}>
                          Annulé: {fmtDateTime(o.cancelled_at)}
                          {o.cancelled_by ? ` · ${o.cancelled_by}` : ""}
                          {o.cancellation_summary ? (
                            <div className="text-[10px] text-[#7A3324] mt-0.5 italic">{o.cancellation_summary}</div>
                          ) : null}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => printKitchenSheet(o)}
                          title="Fiche cuisine (sans prix, photos + quantités)"
                          data-testid={`kitchen-sheet-${o.reference}`}
                        >
                          <ChefHat className="w-4 h-4 text-[#8C6B2F]" />
                        </Button>
                        <Link to={`/etiquettes?order=${o.id}`}>
                          <Button variant="ghost" size="icon" title="Imprimer les étiquettes" data-testid={`label-order-${o.reference}`}>
                            <Tag className="w-4 h-4" />
                          </Button>
                        </Link>
                        {o.status === "PLANIFIE" && (
                          <Button variant="ghost" size="icon" onClick={() => changeStatus(o, "EN_COURS")} title="Démarrer" data-testid={`start-order-${o.reference}`}>
                            <Play className="w-4 h-4 text-[#1F4E3D]" />
                          </Button>
                        )}
                        {o.status === "EN_COURS" && (
                          <Button variant="ghost" size="icon" onClick={() => changeStatus(o, "TERMINE")} title="Terminer" data-testid={`complete-order-${o.reference}`}>
                            <CheckCircle2 className="w-4 h-4 text-[#2D6A4F]" />
                          </Button>
                        )}
                        {/* Phase 87x — Bouton "Finaliser & Expédier" 1-clic pour les OFs web.
                            Clôture l'OF + génère le BL immédiatement. */}
                        {getOrderType(o).kind === "WEB"
                          && (o.status === "PLANIFIE" || o.status === "EN_COURS" || o.status === "TERMINE")
                          && !clientOrderMap[o.client_order_id]?.bl_id
                          && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => finalizeAndShip(o)}
                              disabled={finalizingId === o.id}
                              title="Finaliser cet OF web et générer le BL (1 clic)"
                              data-testid={`finalize-ship-${o.reference}`}
                              className="text-[#8C6B2F] hover:bg-[#FBF4E9]"
                            >
                              <PackageCheck className="w-4 h-4" />
                            </Button>
                          )}
                        {(o.status === "PLANIFIE" || o.status === "EN_COURS" || o.status === "TERMINE") && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => changeStatus(o, "ANNULE")}
                            title={o.status === "TERMINE" ? "Annuler (supprime aussi les lots créés du stock)" : "Annuler"}
                          >
                            <XCircle className="w-4 h-4 text-[#C05C47]" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => remove(o.id)}
                          title={
                            ["TERMINE", "EN_COURS"].includes(o.status)
                              ? "Supprimer (l'OF sera d'abord annulé et ses lots retirés du stock)"
                              : "Supprimer définitivement"
                          }
                          data-testid={`order-delete-${o.id}`}
                        >
                          <Trash2 className="w-4 h-4 text-[#C05C47]" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                  {isExpanded && lines.map((ln, idx) => {
                    const p = productMap[ln.product_id];
                    const lineStats = lineStatsByLotNumber[ln.lot_number];
                    const editable = o.status === "PLANIFIE" || o.status === "EN_COURS";
                    const isEditingThis = editingDlcKey === `${o.id}|${idx}`;
                    const isEditingQtyThis = editingQtyKey === `${o.id}|${idx}`;
                    return (
                      <TableRow key={`${o.id}-${ln.id || idx}`} className="bg-[#FAFAF7] hover:bg-[#F4F4F0]" data-testid={`order-line-${o.reference}-${idx}`}>
                        <TableCell></TableCell>
                        <TableCell className="font-mono text-xs text-[#575A57] pl-8">
                          <Badge variant="outline" className="bg-white border-[#E5E5E0] text-[#575A57] text-[10px] font-mono">
                            {ln.lot_number || "—"}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs">{p?.name || "—"} <span className="text-[#A3A6A3] font-mono">({p?.sku})</span></TableCell>
                        <TableCell className="text-right font-mono text-xs">
                          {isEditingQtyThis ? (
                            <span className="inline-flex items-center gap-1 justify-end">
                              <Input
                                type="number"
                                min="0"
                                step="0.001"
                                value={qtyDraft}
                                onChange={(e) => setQtyDraft(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") saveQty(o, idx);
                                  if (e.key === "Escape") setEditingQtyKey(null);
                                }}
                                className="h-7 text-xs w-[80px] text-right"
                                autoFocus
                                data-testid={`qty-edit-input-${o.reference}-${idx}`}
                              />
                              <Button
                                size="sm"
                                className="h-7 bg-[#1F4E3D] hover:bg-[#173B2E]"
                                onClick={() => saveQty(o, idx)}
                                data-testid={`qty-edit-save-${o.reference}-${idx}`}
                              >
                                OK
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 text-[#A3A6A3]"
                                onClick={() => setEditingQtyKey(null)}
                                data-testid={`qty-edit-cancel-${o.reference}-${idx}`}
                              >
                                ✕
                              </Button>
                            </span>
                          ) : editable ? (
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); startEditQty(o, idx); }}
                              className="font-mono text-xs hover:underline hover:text-[#1F4E3D] cursor-pointer"
                              title="Cliquer pour modifier la quantité"
                              data-testid={`qty-edit-trigger-${o.reference}-${idx}`}
                            >
                              {fmtNum(ln.quantity_target, p?.unit)}
                            </button>
                          ) : (
                            fmtNum(ln.quantity_target, p?.unit)
                          )}
                        </TableCell>
                        <TableCell></TableCell>
                        <TableCell></TableCell>
                        <TableCell>
                          {lineStats ? (
                            <Link
                              to={`/stocks?lot=${encodeURIComponent(ln.lot_number)}`}
                              className="inline-flex items-center gap-1.5 text-xs font-mono hover:underline"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Package className="w-3 h-3 text-[#1F4E3D]" />
                              <span className="font-semibold text-[#1F4E3D]">{lineStats.remaining}</span>
                              <span className="text-[#A3A6A3]">/ {lineStats.total}</span>
                            </Link>
                          ) : (
                            <span className="text-xs text-[#A3A6A3]">—</span>
                          )}
                        </TableCell>
                        <TableCell className="text-xs">
                          {isEditingThis ? (
                            <span className="inline-flex items-center gap-1">
                              <Input
                                type="date"
                                value={dlcDraft}
                                onChange={(e) => setDlcDraft(e.target.value)}
                                className="h-7 text-xs w-[140px]"
                                data-testid={`dlc-edit-input-${o.reference}-${idx}`}
                              />
                              <Button size="sm" className="h-7 bg-[#1F4E3D] hover:bg-[#173B2E]" onClick={() => saveDlc(o, idx)} data-testid={`dlc-edit-save-${o.reference}-${idx}`}>OK</Button>
                              <Button size="sm" variant="ghost" className="h-7" onClick={() => setEditingDlcKey(null)}><XCircle className="w-3.5 h-3.5" /></Button>
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1">
                              {renderDlcOf(ln, o, p)}
                              {editable && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-5 w-5 ml-0.5 text-[#8C6B2F]"
                                  onClick={() => startEditDlc(o, idx)}
                                  title="Modifier la DLC (utile en cas de fortes chaleurs)"
                                  data-testid={`dlc-edit-btn-${o.reference}-${idx}`}
                                >
                                  <CalendarClock className="w-3 h-3" />
                                </Button>
                              )}
                            </span>
                          )}
                        </TableCell>
                        <TableCell></TableCell>
                      </TableRow>
                    );
                  })}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
